/**
 * Maps API payloads ↔ UI state for Rewards Engine.
 * Field names must match backend exactly on the wire.
 */

import { deriveLevelStatus, getAdminDisplayName, coinDraftToNumber, normalizeCoinDraft, parseWholeNumber, finalizeDailyLimit, finalizeMonthlyLimit, finalizeExpiryDays, validateEventExpiry } from "./rewardsUtils";
import {
  EARNING_FREQUENCY,
  getLimitsMode,
  getLimitsModeForUiKey,
  getUiFrequencyKey,
  getUiFrequencyOption,
  inferLimitsFromEvent,
} from "./rewardsConstants";

function parseLimitValue(value) {
  return value === "" ? null : parseWholeNumber(value, { min: 1 });
}

function parseDailyLimitValue(value) {
  return value === "" || value == null ? null : finalizeDailyLimit(value);
}

function parseMonthlyLimitValue(value, dailyLimit) {
  return value === "" || value == null ? null : finalizeMonthlyLimit(value, dailyLimit);
}

function mapDailyMonthlyLimitsPayload(cfg, field, value) {
  const daily =
    field === "dailyLimit" ? parseDailyLimitValue(value) : parseDailyLimitValue(cfg.dailyLimit);
  const monthly =
    field === "monthlyLimit"
      ? parseMonthlyLimitValue(value, daily)
      : parseMonthlyLimitValue(cfg.monthlyLimit, daily);
  return {
    dailyLimit: daily,
    monthlyLimit: monthly,
    cooldownDays: null,
  };
}

// ─── Shared list extraction ──────────────────────────────────────────────────

export function extractListItems(response) {
  const data = response?.data;
  if (!data) return [];

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.docs)) return data.docs;
  if (Array.isArray(data.data)) return data.data;
  if (data.data && Array.isArray(data.data.docs)) return data.data.docs;

  return [];
}

export function extractSingleItem(response) {
  const data = response?.data;
  if (!data) return null;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

export function mapCategoryEventNamesFromResponse(response) {
  const body = response?.data;
  if (!body) return [];

  const candidates = [
    body,
    body.data,
    body.docs,
    body.categoryEvents,
    body.data?.docs,
    body.data?.categoryEvents,
  ];
  const items = candidates.find(Array.isArray) || [];

  return [
    ...new Set(
      items
        .map((item) => {
          if (typeof item === "string") return item.trim();
          return typeof item?.name === "string" ? item.name.trim() : "";
        })
        .filter(Boolean)
    ),
  ];
}

// ─── Global Settings ─────────────────────────────────────────────────────────

export function mapGlobalSettingsFromApi(apiData) {
  if (!apiData) return null;

  return {
    _id: apiData._id,
    encashmentLockDays: apiData.encashmentLockDays ?? 0,
    redemptionPercent: apiData.redemptionPercentage ?? 0,
    monthlyReferralCoinCap: apiData.monthlyReferralCoinCap ?? 0,
    updatedAt: apiData.updatedAt,
    updatedBy: apiData.updatedBy,
  };
}

export function mapGlobalSettingsToApi(uiSettings) {
  return {
    monthlyReferralCoinCap: parseWholeNumber(uiSettings.monthlyReferralCoinCap, {
      min: 0,
    }),
    encashmentLockDays: parseWholeNumber(uiSettings.encashmentLockDays, { min: 0 }),
    redemptionPercentage: uiSettings.redemptionPercent,
  };
}

export function extractGlobalSettingsFromListResponse(response) {
  const data = response?.data;
  if (!data) return null;

  if (Array.isArray(data)) {
    return mapGlobalSettingsFromApi(data[0]);
  }

  if (Array.isArray(data.docs)) {
    return mapGlobalSettingsFromApi(data.docs[0]);
  }

  if (data.data && typeof data.data === "object") {
    return mapGlobalSettingsFromApi(data.data);
  }

  if (
    data.monthlyReferralCoinCap != null ||
    data.encashmentLockDays != null ||
    data.redemptionPercentage != null
  ) {
    return mapGlobalSettingsFromApi(data);
  }

  return null;
}

export function extractGlobalSettingsFromUpdateResponse(response) {
  const data = response?.data;
  if (!data) return null;

  if (data.data && typeof data.data === "object") {
    return mapGlobalSettingsFromApi(data.data);
  }

  return mapGlobalSettingsFromApi(data);
}

// ─── Event Configuration ─────────────────────────────────────────────────────

export function mapEventFromApi(apiData) {
  if (!apiData) return null;

  const earningFrequency = apiData.earningFrequency || EARNING_FREQUENCY.ONCE;
  const limits = inferLimitsFromEvent({
    earningFrequency,
    dailyLimit: apiData.dailyLimit,
    monthlyLimit: apiData.monthlyLimit,
    cooldownDays: apiData.cooldownDays,
  });

  return {
    _id: apiData._id,
    eventKey: apiData.eventKey,
    name: apiData.name ?? "",
    desc: apiData.description ?? "",
    coins: apiData.coinValue ?? 0,
    cat: apiData.category || apiData.eventCategory || "Events",
    expiryDays: apiData.expiryDays ?? 1,
    earningFrequency,
    dailyLimit:
      limits === "daily" || limits === "daily_monthly" ? apiData.dailyLimit ?? null : null,
    monthlyLimit:
      limits === "monthly" || limits === "daily_monthly" ? apiData.monthlyLimit ?? null : null,
    cooldownDays: limits === "cooldown" ? apiData.cooldownDays ?? null : null,
    notificationEnabled:
      (apiData.isNotificationEnabled === true || apiData.isNotificationEnabled === 1) &&
      Boolean((apiData.notificationTitle ?? apiData.notification?.title ?? "").trim()) &&
      Boolean((apiData.notificationBody ?? apiData.notification?.body ?? "").trim()),
    notificationTitle: apiData.notificationTitle ?? apiData.notification?.title ?? "",
    notificationBody: apiData.notificationBody ?? apiData.notification?.body ?? "",

    version: 1,
  };
}

export function mapEarningFrequencyPayload(cfg, uiKey = getUiFrequencyKey(cfg)) {
  const option = getUiFrequencyOption(uiKey);
  const limits = getLimitsModeForUiKey(uiKey);
  const isLimitMode = limits === "daily" || limits === "monthly" || limits === "daily_monthly";

  const payload = {
    dailyLimit: null,
    monthlyLimit: null,
    cooldownDays: null,
  };

  if (!isLimitMode) {
    payload.earningFrequency = option?.apiFrequency || cfg.earningFrequency;
  } else {
    payload.earningFrequency = "MONTHLY";
  }

  if (limits === "daily" || limits === "daily_monthly") {
    payload.dailyLimit = parseDailyLimitValue(cfg.dailyLimit);
  }
  if (limits === "monthly" || limits === "daily_monthly") {
    payload.monthlyLimit = parseMonthlyLimitValue(
      cfg.monthlyLimit,
      parseDailyLimitValue(cfg.dailyLimit)
    );
  }
  if (limits === "cooldown") {
    payload.cooldownDays =
      cfg.cooldownDays === "" || cfg.cooldownDays == null
        ? 90
        : parseWholeNumber(normalizeCoinDraft(cfg.cooldownDays), { min: 1 });
  }

  return payload;
}

export function mergeEventAfterUpdate(localCfg, apiCfg) {
  if (!apiCfg) return localCfg;

  const merged = {
    ...localCfg,
    ...apiCfg,
    _lastCoins: localCfg._lastCoins,
    // Keep local notification if API response omits these fields
    notificationEnabled: localCfg.notificationEnabled ?? apiCfg.notificationEnabled ?? false,
    notificationTitle: localCfg.notificationTitle ?? apiCfg.notificationTitle ?? "",
    notificationBody: localCfg.notificationBody ?? apiCfg.notificationBody ?? "",
    version: (localCfg.version || 1) + 1,
  };

  if (getLimitsMode(merged) === "daily_monthly") {
    if (merged.dailyLimit == null && localCfg.dailyLimit != null) {
      merged.dailyLimit = localCfg.dailyLimit;
    }
    if (merged.monthlyLimit == null && localCfg.monthlyLimit != null) {
      merged.monthlyLimit = localCfg.monthlyLimit;
    }
    if (merged.dailyLimit == null && merged.monthlyLimit == null) {
      merged.monthlyLimit = 10;
    }
  }

  return merged;
}

export function mapEventsFromListResponse(response) {
  return extractListItems(response)
    .map(mapEventFromApi)
    .filter(Boolean);
}

export function mapEventFromMutationResponse(response) {
  return mapEventFromApi(extractSingleItem(response));
}

export function buildEventConfigMap(events) {
  return events.reduce((acc, event) => {
    acc[event.eventKey] = event;
    return acc;
  }, {});
}

export function buildCategoryOrderFromEvents(events) {
  const order = [];
  const seen = new Set();

  events.forEach((event) => {
    const cat = event.cat || "Events";
    if (!seen.has(cat)) {
      seen.add(cat);
      order.push(cat);
    }
  });

  return order.length ? order : ["Events"];
}

export function applyEventFieldUpdate(cfg, field, value) {
  const next = { ...cfg };

  if (field === "name") {
    next.name = value.trim() || cfg.name;
  } else if (field === "desc") {
    next.desc = value;
  } else if (field === "coins") {
    const draft = normalizeCoinDraft(value);
    const numeric = coinDraftToNumber(draft);
    if (numeric === 0 && Number(cfg.coins) > 0) {
      next._lastCoins = cfg.coins;
    } else if (numeric > 0) {
      delete next._lastCoins;
    }
    // Keep string draft while typing so clearing never forces a leading "0"
    next.coins = draft;
  } else if (field === "dailyLimit") {
    next.dailyLimit = value === "" ? "" : normalizeCoinDraft(value);
  } else if (field === "monthlyLimit") {
    next.monthlyLimit = value === "" ? "" : normalizeCoinDraft(value);
  } else if (field === "cooldownDays") {
    next.cooldownDays = value === "" ? "" : normalizeCoinDraft(value);
  } else if (field === "expiryDays") {
    next.expiryDays = value === "" || value == null ? "" : normalizeCoinDraft(value);
  } else if (field === "notificationEnabled") {
    next.notificationEnabled = Boolean(value);
  } else if (field === "notificationTitle") {
    next.notificationTitle = value;
  } else if (field === "notificationBody") {
    next.notificationBody = value;
  }

  next.version = (next.version || 1) + 1;
  return next;
}

export function applyEarningFrequencyUpdate(cfg, uiKey) {
  const option = getUiFrequencyOption(uiKey);
  const limits = getLimitsModeForUiKey(uiKey);
  const isLimitMode = limits === "daily" || limits === "monthly" || limits === "daily_monthly";

  const next = { ...cfg };
  if (!isLimitMode) {
    next.earningFrequency = option?.apiFrequency || uiKey;
  } else {
    // Limits alone define daily/monthly mode. Clear COOLDOWN so the UI key
    // does not snap back after switching from Cooldown period.
    if (next.earningFrequency === EARNING_FREQUENCY.COOLDOWN) {
      next.earningFrequency = EARNING_FREQUENCY.UNLIMITED;
    }
  }

  if (limits === "cooldown") {
    next.cooldownDays = cfg.cooldownDays ?? 90;
    next.dailyLimit = null;
    next.monthlyLimit = null;
  } else if (limits === "daily") {
    next.cooldownDays = null;
    next.monthlyLimit = null;
    if (next.dailyLimit == null) next.dailyLimit = 5;
  } else if (limits === "monthly") {
    next.cooldownDays = null;
    next.dailyLimit = null;
    if (next.monthlyLimit == null) next.monthlyLimit = 10;
  } else if (limits === "daily_monthly") {
    next.cooldownDays = null;
    if (next.dailyLimit == null && next.monthlyLimit == null) {
      next.dailyLimit = 5;
      next.monthlyLimit = 50;
    }
  } else {
    next.dailyLimit = null;
    next.monthlyLimit = null;
    next.cooldownDays = null;
  }

  next.version = (next.version || 1) + 1;
  return next;
}

export function applyEventToggle(cfg, defaultActiveCoins = 10) {
  const next = { ...cfg };
  const currentCoins = coinDraftToNumber(next.coins);

  if (currentCoins > 0) {
    next._lastCoins = currentCoins;
    next.coins = 0;
  } else {
    next.coins = next._lastCoins || defaultActiveCoins;
    delete next._lastCoins;
  }

  next.version = (next.version || 1) + 1;
  return next;
}

export function mapEventFieldToApiPayload(field, cfg, value) {
  if (field === "name") {
    return { name: value.trim() || cfg.name };
  }
  if (field === "desc") {
    return { description: value };
  }
  if (field === "coins") {
    return { coinValue: coinDraftToNumber(value) };
  }
  if (field === "dailyLimit" || field === "monthlyLimit") {
    if (getLimitsMode(cfg) === "daily_monthly") {
      return mapDailyMonthlyLimitsPayload(cfg, field, value);
    }
    if (field === "dailyLimit") {
      return { dailyLimit: parseLimitValue(value) };
    }
    return { monthlyLimit: parseLimitValue(value) };
  }
  if (field === "cooldownDays") {
    const days =
      value === "" || value == null
        ? null
        : parseWholeNumber(normalizeCoinDraft(value), { min: 1 });
    return { cooldownDays: days };
  }
  if (field === "expiryDays") {
    return { expiryDays: finalizeExpiryDays(value) };
  }

  return {};
}

/** Full event update payload for explicit Save action */
export function mapEventToUpdateApi(cfg) {
  const freqPayload = mapEarningFrequencyPayload(cfg);
  return {
    name: cfg.name,
    description: cfg.desc ?? "",
    coinValue: coinDraftToNumber(cfg.coins),
    ...freqPayload,
    expiryDays: finalizeExpiryDays(cfg.expiryDays),
    isNotificationEnabled: Boolean(cfg.notificationEnabled),
    notificationTitle: cfg.notificationTitle ?? "",
    notificationBody: cfg.notificationBody ?? "",
  };
}

export function getEventSaveSignature(cfg) {
  if (!cfg) return null;
  return {
    name: cfg.name ?? "",
    desc: cfg.desc ?? "",
    coins: coinDraftToNumber(cfg.coins),
    earningFrequency: cfg.earningFrequency ?? null,
    dailyLimit: cfg.dailyLimit ?? null,
    monthlyLimit: cfg.monthlyLimit ?? null,
    cooldownDays:
      cfg.cooldownDays === "" || cfg.cooldownDays == null
        ? null
        : parseWholeNumber(normalizeCoinDraft(cfg.cooldownDays), { min: 1 }),
    expiryDays:
      validateEventExpiry(cfg).length > 0
        ? String(cfg.expiryDays ?? "")
        : finalizeExpiryDays(cfg.expiryDays),
    notificationEnabled: Boolean(cfg.notificationEnabled),
    notificationTitle: cfg.notificationTitle ?? "",
    notificationBody: cfg.notificationBody ?? "",
  };
}

export function getGlobalSettingsSaveSignature(settings) {
  if (!settings) return null;
  return {
    encashmentLockDays: parseWholeNumber(settings.encashmentLockDays, { min: 0 }),
    redemptionPercent: settings.redemptionPercent ?? 0,
    monthlyReferralCoinCap: coinDraftToNumber(settings.monthlyReferralCoinCap),
  };
}

export function getReferralLevelSaveSignature(level) {
  if (!level) return null;
  const firstShiftBonus =
    level.firstShiftBonus === "" || level.firstShiftBonus == null
      ? null
      : coinDraftToNumber(level.firstShiftBonus);
  return {
    coins: coinDraftToNumber(level.coins),
    signupExpiryDays: level.signupExpiryDays ?? null,
    firstShiftBonus,
    firstShiftExpiryDays: level.firstShiftExpiryDays ?? null,
    status: level.status,
    isActive: level.isActive !== false && level.status === "active",
    notificationEnabled: Boolean(level.notificationEnabled),
    notificationTitle: level.notificationTitle ?? "",
    notificationBody: level.notificationBody ?? "",

    isFirstShiftNotificationEnabled: Boolean(
      level.isFirstShiftNotificationEnabled,
    ),

    firstShiftNotificationTitle: level.firstShiftNotificationTitle ?? "",

    firstShiftNotificationBody: level.firstShiftNotificationBody ?? "",
  };
}

export function mapReferralLevelToUpdateApi(level) {
  const firstShiftBonus =
    level.firstShiftBonus === "" || level.firstShiftBonus == null
      ? null
      : coinDraftToNumber(level.firstShiftBonus);

  return {
    signupCoins: coinDraftToNumber(level.coins),
    signupExpiryDays: nullishExpiryToApi(level.signupExpiryDays),
    firstShiftCoins: nullishCoinsToApi(firstShiftBonus),
    firstShiftExpiryDays: nullishExpiryToApi(level.firstShiftExpiryDays),
    isActive: level.isActive !== false && level.status === "active",
    isNotificationEnabled: Boolean(level.notificationEnabled),
    notificationTitle: level.notificationTitle ?? "",
    notificationBody: level.notificationBody ?? "",
    isFirstShiftNotificationEnabled: Boolean(
      level.isFirstShiftNotificationEnabled,
    ),

    firstShiftNotificationTitle: level.firstShiftNotificationTitle ?? "",

    firstShiftNotificationBody: level.firstShiftNotificationBody ?? "",
  };
}

export function mapEventToCreateApi({
  eventKey,
  name,
  description,
  coinValue,
  earningFrequency,
  dailyLimit,
  monthlyLimit,
  cooldownDays,
  expiryDays,
  isNotificationEnabled = false,
  notificationTitle = "",
  notificationBody = "",
}) {
  return {
    eventKey,
    name,
    description,
    coinValue,
    earningFrequency,
    dailyLimit,
    monthlyLimit,
    cooldownDays,
    expiryDays,
    isNotificationEnabled: Boolean(isNotificationEnabled),
    notificationTitle: notificationTitle ?? "",
    notificationBody: notificationBody ?? "",
  };
}

export function getNextCustomEventKey(events) {
  const customKeys = events
    .map((event) => event.eventKey)
    .filter((key) => /^CUSTOM_EVENT_\d+$/.test(key))
    .map((key) => Number(key.replace("CUSTOM_EVENT_", "")));

  const next = customKeys.length ? Math.max(...customKeys) + 1 : 1;
  return `CUSTOM_EVENT_${next}`;
}

function slugifyEventKeyBase(name) {
  return (name || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Derives a unique, human-readable eventKey from the event name (e.g. "Resume Uploaded" -> RESUME_UPLOADED) */
export function buildEventKeyFromName(name, existingEvents) {
  const existingKeys = new Set(existingEvents.map((event) => event.eventKey));
  const base = slugifyEventKeyBase(name);

  if (!base) return getNextCustomEventKey(existingEvents);
  if (!existingKeys.has(base)) return base;

  let suffix = 2;
  while (existingKeys.has(`${base}_${suffix}`)) {
    suffix += 1;
  }
  return `${base}_${suffix}`;
}

// ─── Referral Levels ─────────────────────────────────────────────────────────

function nullishExpiryToApi(value) {
  return value == null ? 0 : value;
}

function expiryFromApi(value) {
  return value == null || value === 0 ? null : value;
}

function nullishCoinsToApi(value) {
  return value == null ? 0 : value;
}

function coinsFromApi(value) {
  return value == null ? 0 : value;
}

export function mapReferralLevelFromApi(apiData, fallbackLevel) {
  if (!apiData) return null;

  const signupCoins = coinsFromApi(apiData.signupCoins);
  const isActive = apiData.isActive !== false;

  return {
    _id: apiData._id,
    level: apiData.level ?? apiData.levelNumber ?? fallbackLevel,
    coins: signupCoins,
    signupExpiryDays: expiryFromApi(apiData.signupExpiryDays),
    firstShiftBonus:
      apiData.firstShiftCoins == null || apiData.firstShiftCoins === 0
        ? null
        : apiData.firstShiftCoins,
    firstShiftExpiryDays: expiryFromApi(apiData.firstShiftExpiryDays),
    isActive,
    status: isActive ? "active" : "paused",
    nameVisible: (apiData.level ?? apiData.levelNumber ?? fallbackLevel) === 1,
    notificationEnabled:
      (apiData.isNotificationEnabled === true ||
        apiData.isNotificationEnabled === 1) &&
      Boolean(
        (apiData.notificationTitle ?? apiData.notification?.title ?? "").trim(),
      ) &&
      Boolean(
        (apiData.notificationBody ?? apiData.notification?.body ?? "").trim(),
      ),
    notificationTitle:
      apiData.notificationTitle ?? apiData.notification?.title ?? "",
    notificationBody:
      apiData.notificationBody ?? apiData.notification?.body ?? "",

    isFirstShiftNotificationEnabled:
      (apiData.isFirstShiftNotificationEnabled === true ||
        apiData.isFirstShiftNotificationEnabled === 1) &&
      Boolean((apiData.firstShiftNotificationTitle ?? "").trim()) &&
      Boolean((apiData.firstShiftNotificationBody ?? "").trim()),

    firstShiftNotificationTitle: apiData.firstShiftNotificationTitle ?? "",

    firstShiftNotificationBody: apiData.firstShiftNotificationBody ?? "",
    version: 1,
    updatedAt: apiData.updatedAt,
    updatedBy: apiData.updatedBy,
  };
}

export function mapReferralLevelsFromListResponse(response) {
  const items = extractListItems(response);

  return items
    .map((item, index) => mapReferralLevelFromApi(item, index + 1))
    .filter(Boolean)
    .sort((a, b) => a.level - b.level);
}

export function mapReferralLevelFromMutationResponse(response) {
  const item = extractSingleItem(response);
  return mapReferralLevelFromApi(item, item?.level ?? item?.levelNumber);
}

export function mapReferralLevelToCreateApi({
  coins = 1,
  signupExpiryDays = null,
  firstShiftBonus = null,
  firstShiftExpiryDays = null,
  isNotificationEnabled = false,
  notificationTitle = "",
  notificationBody = "",

  isFirstShiftNotificationEnabled = false,
  firstShiftNotificationTitle = "",
  firstShiftNotificationBody = "",
}) {
  return {
    signupCoins: coins,
    signupExpiryDays: nullishExpiryToApi(signupExpiryDays),
    firstShiftCoins: nullishCoinsToApi(firstShiftBonus),
    firstShiftExpiryDays: nullishExpiryToApi(firstShiftExpiryDays),
    isNotificationEnabled: Boolean(isNotificationEnabled),
    notificationTitle: notificationTitle ?? "",
    notificationBody: notificationBody ?? "",

    isFirstShiftNotificationEnabled: Boolean(isFirstShiftNotificationEnabled),
    firstShiftNotificationTitle: firstShiftNotificationTitle ?? "",
    firstShiftNotificationBody: firstShiftNotificationBody ?? "",
  };
}

export function mapReferralLevelFieldToApi(field, value) {
  if (field === "coins") {
    return { signupCoins: coinDraftToNumber(value) };
  }
  if (field === "signupExpiryDays") {
    return {
      signupExpiryDays:
        value === "" ? 0 : parseWholeNumber(value, { min: 1 }),
    };
  }
  if (field === "firstShiftBonus") {
    return {
      firstShiftCoins: value === "" ? 0 : coinDraftToNumber(value),
    };
  }
  if (field === "firstShiftExpiryDays") {
    return {
      firstShiftExpiryDays:
        value === "" ? 0 : parseWholeNumber(value, { min: 1 }),
    };
  }

  return {};
}

export function applyReferralLevelFieldUpdate(level, field, value) {
  const next = { ...level };

  if (field === "coins") {
    const draft = normalizeCoinDraft(value);
    const numeric = coinDraftToNumber(draft);
    next.coins = draft;
    next.status = deriveLevelStatus(numeric, level.status);
    next.isActive = next.status === "active";
  } else if (field === "signupExpiryDays") {
    next.signupExpiryDays =
      value === "" ? null : parseWholeNumber(value, { min: 1 });
  } else if (field === "firstShiftBonus") {
    next.firstShiftBonus = normalizeCoinDraft(value);
  } else if (field === "firstShiftExpiryDays") {
    next.firstShiftExpiryDays =
      value === "" ? null : parseWholeNumber(value, { min: 1 });
  } else if (field === "notificationEnabled") {
    next.notificationEnabled = Boolean(value);
  } else if (field === "notificationTitle") {
    next.notificationTitle = value;
  } else if (field === "notificationBody") {
    next.notificationBody = value;
  } else if (field === "isFirstShiftNotificationEnabled") {
    next.isFirstShiftNotificationEnabled = Boolean(value);
  } else if (field === "firstShiftNotificationTitle") {
    next.firstShiftNotificationTitle = value;
  } else if (field === "firstShiftNotificationBody") {
    next.firstShiftNotificationBody = value;
  }

  next.version = (next.version || 1) + 1;
  return next;
}

export function applyReferralLevelToggle(level) {
  const next = { ...level };

  if (next.status === "active") {
    next.status = "paused";
    next.isActive = false;
  } else {
    next.status = "active";
    next.isActive = true;
  }

  next.version = (next.version || 1) + 1;
  return next;
}

export function mapReferralLevelToggleToApi(level) {
  return { isActive: level.isActive !== false };
}

export function mergeReferralLevelAfterUpdate(localLevel, apiLevel) {
  if (!apiLevel) return localLevel;

  return {
    ...localLevel,
    ...apiLevel,
    notificationEnabled:
      localLevel.notificationEnabled ?? apiLevel.notificationEnabled ?? false,
    notificationTitle:
      localLevel.notificationTitle ?? apiLevel.notificationTitle ?? "",
    notificationBody:
      localLevel.notificationBody ?? apiLevel.notificationBody ?? "",

    isFirstShiftNotificationEnabled:
      localLevel.isFirstShiftNotificationEnabled ??
      apiLevel.isFirstShiftNotificationEnabled ??
      false,

    firstShiftNotificationTitle:
      localLevel.firstShiftNotificationTitle ??
      apiLevel.firstShiftNotificationTitle ??
      "",

    firstShiftNotificationBody:
      localLevel.firstShiftNotificationBody ??
      apiLevel.firstShiftNotificationBody ??
      "",
    version: (localLevel.version || 1) + 1,
  };
}

// ─── Audit Log ───────────────────────────────────────────────────────────────

const AUDIT_LOG_PAGE_SIZE = 50;

const AUDIT_FIELD_LABELS = {
  monthlyReferralCoinCap: "Monthly referral coin cap",
  encashmentLockDays: "Encashment lock",
  redemptionPercentage: "Percentage of coin redemption",
  coinValue: "coins",
  earningFrequency: "frequency",
  dailyLimit: "daily limit",
  monthlyLimit: "monthly limit",
  cooldownDays: "cooldown",
  expiryDays: "expiry",
  signupCoins: "coins on signup",
  signupExpiryDays: "signup expiry",
  firstShiftCoins: "first-shift bonus",
  firstShiftExpiryDays: "first-shift expiry",
  isActive: "status",
  name: "name",
  description: "reward history name",
  isNotificationEnabled: "notification enabled",
  notificationTitle: "notification title",
  notificationBody: "notification body",

  isFirstShiftNotificationEnabled: "first-shift notification enabled",

  firstShiftNotificationTitle: "first-shift notification title",

  firstShiftNotificationBody: "first-shift notification body",
};

const AUDIT_IGNORE_FIELDS = new Set([
  "_id",
  "__v",
  "createdAt",
  "updatedAt",
  "updatedBy",
  "eventKey",
  "documentID",
]);

function mapAuditActor(actor) {
  if (!actor) return "Admin";
  if (typeof actor === "string") return actor;
  return getAdminDisplayName(actor);
}

function formatFrequencyLabel(record) {
  if (!record) return "none";
  const freq = record.earningFrequency;
  if (freq === "ONCE") return "One-time only";
  if (freq === "COOLDOWN") return "Cooldown period";
  if (freq === "UNLIMITED") {
    if (record.dailyLimit != null || record.monthlyLimit != null) {
      return "Daily/monthly limit";
    }
    return "Unlimited";
  }
  if (freq === "MONTHLY") return "Daily/monthly limit";
  if (freq === "DAILY") return "Daily limit";
  return freq || "none";
}

function formatAuditFieldValue(field, value, record) {
  if (value == null || value === "") return "none";

  if (field === "earningFrequency") return formatFrequencyLabel(record);
  if (field === "isActive") return value ? "Active" : "Paused";
  if (field === "encashmentLockDays" || field === "expiryDays") {
    return `${value} days`;
  }
  if (field === "signupExpiryDays" || field === "firstShiftExpiryDays") {
    return value === 0 ? "never" : `${value} days`;
  }
  if (field === "redemptionPercentage") return `${value}%`;
  if (field === "dailyLimit" || field === "monthlyLimit" || field === "cooldownDays") {
    return value === 0 ? "none" : String(value);
  }
  if (field === "firstShiftCoins" || field === "signupCoins") {
    return value === 0 ? "none" : String(value);
  }

  return String(value);
}

function getChangedAuditFields(oldValue, newValue) {
  if (!oldValue || !newValue) return [];

  const keys = new Set([...Object.keys(oldValue), ...Object.keys(newValue)]);

  return [...keys].filter((key) => {
    if (AUDIT_IGNORE_FIELDS.has(key)) return false;
    return JSON.stringify(oldValue[key]) !== JSON.stringify(newValue[key]);
  });
}

/** Parses backend `name` (comma-separated changed-field list) into displayable fields. */
function getAuditFieldsFromName(name, record) {
  if (typeof name !== "string" || !name.trim()) return [];

  return name
    .split(",")
    .map((field) => field.trim())
    .filter((field) => field && !AUDIT_IGNORE_FIELDS.has(field))
    .filter((field) => !record || field in record || field in AUDIT_FIELD_LABELS);
}

function formatAuditChangeLine(module, field, newValue, record) {
  const label = AUDIT_FIELD_LABELS[field] || field;
  const formatted = formatAuditFieldValue(field, newValue[field], record);

  if (module === "EVENT") {
    const eventName = newValue.name || "Event";
    const eventKey = newValue.eventKey ? ` (${newValue.eventKey})` : "";
    return `${eventName}${eventKey} — ${label} → ${formatted}`;
  }

  if (module === "REFERRAL_LEVEL") {
    const level = newValue.level ?? "?";
    return `Level ${level} ${label} → ${formatted}`;
  }

  if (field === "encashmentLockDays") {
    return `${label} → ${formatted}`;
  }

  return `${label} → ${formatted}`;
}

function mapAuditSummary(apiData) {
  const { oldValue, newValue, module } = apiData;
  const record =
    newValue && typeof newValue === "object"
      ? newValue
      : oldValue && typeof oldValue === "object"
        ? oldValue
        : null;

  let fields = [];
  if (oldValue && newValue && typeof oldValue === "object" && typeof newValue === "object") {
    fields = getChangedAuditFields(oldValue, newValue);
  }

  // Diff empty (e.g. same value re-saved on blur) — fall back to backend's changed-field list
  if (fields.length === 0) {
    fields = getAuditFieldsFromName(apiData.name, record);
  }

  if (fields.length > 0 && record) {
    return fields
      .map((field) => formatAuditChangeLine(module, field, record, record))
      .join("; ");
  }

  if (typeof apiData.summary === "string" && apiData.summary.trim()) return apiData.summary;

  return "Settings updated";
}

export function extractAuditLogItems(response) {
  const body = response?.data;
  if (!body) return [];

  if (Array.isArray(body.logs)) return body.logs;
  if (body.data && Array.isArray(body.data.logs)) return body.data.logs;

  return extractListItems(response);
}

export function mapAuditLogFromApi(apiData) {
  if (!apiData) return null;

  return {
    _id: apiData._id,
    at: apiData.createdAt || apiData.updatedAt || apiData.at || null,
    actor: mapAuditActor(
      apiData.updatedBy || apiData.user || apiData.admin || apiData.performedBy || apiData.actor
    ),
    summary: mapAuditSummary(apiData),
  };
}

export function mapAuditLogsFromListResponse(response) {
  return extractAuditLogItems(response).map(mapAuditLogFromApi).filter(Boolean);
}

export function extractAuditLogPagination(response, { page, limit } = {}) {
  const body = response?.data;
  if (!body) return { hasMore: false, page: page || 1 };

  const payload =
    body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : body;
  const items = extractAuditLogItems(response);
  const currentPage = payload.page ?? payload.currentPage ?? page ?? 1;
  const pageSize = payload.limit ?? limit ?? AUDIT_LOG_PAGE_SIZE;
  const total = payload.total ?? payload.totalDocs ?? null;

  let hasMore = false;
  if (total != null) {
    hasMore = currentPage * pageSize < total;
  } else {
    hasMore = items.length >= pageSize;
  }

  return { hasMore, page: currentPage };
}

export function getAuditLogListPayload(page = 1, limit = AUDIT_LOG_PAGE_SIZE) {
  return { page, limit };
}

